import React, { useState, useEffect } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { 
  FileText, 
  User, 
  Edit3, 
  Save, 
  CheckCircle, 
  ZoomIn, 
  ZoomOut,
  ChevronLeft,
  ChevronRight,
  Menu,
  X
} from 'lucide-react';
import { CompactCustomerSelection } from '@/components/CompactCustomerSelection';
import { DynamicFieldValidator } from '@/components/DynamicFieldValidator';
import { ValidationProgressCard } from '@/components/ValidationProgressCard';
import SimplePDFViewer from '@/components/SimplePDFViewer';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

interface MobileReviewInterfaceProps {
  queueItem: any;
  onSave: (data: any) => void;
  onComplete: () => void;
  onBack: () => void;
  fileUrl?: string;
  isPdfFile?: boolean;
}

export function MobileReviewInterface({
  queueItem,
  onSave,
  onComplete,
  onBack,
  fileUrl,
  isPdfFile
}: MobileReviewInterfaceProps) {
  const isMobile = useIsMobile();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('document');
  const [isFieldsPanelOpen, setIsFieldsPanelOpen] = useState(false);
  const [editedData, setEditedData] = useState(queueItem?.extracted_data || {});
  const [pdfZoom, setPdfZoom] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setEditedData(queueItem?.extracted_data || {});
  }, [queueItem]);

  if (!isMobile) {
    return null; // Fallback to desktop interface
  }

  const handleFieldChange = (fieldName: string, value: any) => {
    let processedValue = value;
    
    // Ensure date field is always in YYYY-MM-DD format
    if (fieldName === 'bill_date' && value) {
      const dateObj = new Date(value);
      if (!isNaN(dateObj.getTime())) {
        processedValue = dateObj.toISOString().split('T')[0];
      }
    }
    
    setEditedData(prev => ({
      ...prev,
      [fieldName]: processedValue
    }));
  };

  const handleSave = () => {
    try {
      // Sanitize data to prevent circular reference errors during JSON.stringify
      const sanitizedData = JSON.parse(JSON.stringify(editedData));
      onSave(sanitizedData);
      toast({
        title: "Saved",
        description: "Progress saved successfully",
      });
    } catch (error) {
      console.error('Mobile save error:', error);
      toast({
        title: "Error",
        description: "Failed to save progress",
        variant: "destructive",
      });
    }
  };

  const handleComplete = () => {
    try {
      // Sanitize data to prevent circular reference errors during JSON.stringify
      const sanitizedData = JSON.parse(JSON.stringify(editedData));
      onSave(sanitizedData);
      onComplete();
    } catch (error) {
      console.error('Mobile complete error:', error);
      toast({
        title: "Error",
        description: "Failed to complete review",
        variant: "destructive",
      });
    }
  };

  const handleZoomIn = () => {
    setPdfZoom(prev => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setPdfZoom(prev => Math.max(prev - 0.25, 0.5));
  };

  // Touch gesture handlers for PDF navigation
  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    // Store initial touch position for swipe detection
    e.currentTarget.setAttribute('data-touch-start-x', touch.clientX.toString());
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const startX = parseFloat(e.currentTarget.getAttribute('data-touch-start-x') || '0');
    const endX = e.changedTouches[0].clientX;
    const diffX = startX - endX;

    // Swipe threshold
    if (Math.abs(diffX) > 50) {
      if (diffX > 0) {
        // Swipe left - next page
        setCurrentPage(prev => prev + 1);
      } else {
        // Swipe right - previous page
        setCurrentPage(prev => Math.max(prev - 1, 1));
      }
    }
  };

  return (
    <div className="h-screen flex flex-col bg-background">
      {/* Mobile Header */}
      <Card className="border-b rounded-none">
        <div className="p-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={onBack}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-2 min-w-0">
              <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <span className="text-sm font-medium truncate">{queueItem?.file_name}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">
              {queueItem?.status}
            </Badge>
            <Sheet open={isFieldsPanelOpen} onOpenChange={setIsFieldsPanelOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="sm">
                  <Menu className="h-4 w-4" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full sm:w-[400px]">
                <SheetHeader>
                  <SheetTitle>Review Fields</SheetTitle>
                </SheetHeader>
                <div className="mt-6 space-y-4">
                  <CompactCustomerSelection
                    selectedCustomerId={queueItem?.customer_id}
                    onCustomerSelect={(customer) => {
                      setEditedData(prev => ({
                        ...prev,
                        customer_id: customer?.id || null
                      }));
                    }}
                  />
                  <ValidationProgressCard />
                  <DynamicFieldValidator
                    data={editedData}
                    onFieldChange={handleFieldChange}
                  />
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </Card>

      {/* Mobile Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
        <div className="border-b bg-card">
          <TabsList className="w-full justify-start gap-0 bg-transparent p-0 h-auto">
            <TabsTrigger 
              value="document" 
              className="flex-1 data-[state=active]:bg-background data-[state=active]:shadow-sm rounded-none border-b-2 border-transparent data-[state=active]:border-primary"
            >
              <FileText className="h-4 w-4 mr-1" />
              Document
            </TabsTrigger>
            <TabsTrigger 
              value="fields" 
              className="flex-1 data-[state=active]:bg-background data-[state=active]:shadow-sm rounded-none border-b-2 border-transparent data-[state=active]:border-primary"
            >
              <Edit3 className="h-4 w-4 mr-1" />
              Fields
            </TabsTrigger>
            <TabsTrigger 
              value="customer" 
              className="flex-1 data-[state=active]:bg-background data-[state=active]:shadow-sm rounded-none border-b-2 border-transparent data-[state=active]:border-primary"
            >
              <User className="h-4 w-4 mr-1" />
              Customer
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Document Tab */}
        <TabsContent value="document" className="flex-1 m-0 p-0">
          {fileUrl && isPdfFile ? (
            <div className="h-full flex flex-col">
              {/* PDF Controls */}
              <div className="p-2 bg-card border-b flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={handleZoomOut}>
                    <ZoomOut className="h-3 w-3" />
                  </Button>
                  <span className="text-xs px-2">{Math.round(pdfZoom * 100)}%</span>
                  <Button variant="outline" size="sm" onClick={handleZoomIn}>
                    <ZoomIn className="h-3 w-3" />
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}>
                    <ChevronLeft className="h-3 w-3" />
                  </Button>
                  <span className="text-xs px-2">Page {currentPage}</span>
                  <Button variant="outline" size="sm" onClick={() => setCurrentPage(prev => prev + 1)}>
                    <ChevronRight className="h-3 w-3" />
                  </Button>
                </div>
              </div>
              
              {/* PDF Viewer with Touch Support */}
              <div 
                className="flex-1 overflow-auto"
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
              >
                <SimplePDFViewer 
                  pdfUrl={fileUrl}
                  fileName={queueItem?.file_name}
                />
              </div>
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center text-center p-6">
              <div>
                <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No document preview available</p>
                <p className="text-xs text-muted-foreground mt-1">Switch to Fields tab to review data</p>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Fields Tab */}
        <TabsContent value="fields" className="flex-1 m-0 p-4 overflow-auto">
          <div className="space-y-4">
            <ValidationProgressCard />
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Field Validation</CardTitle>
              </CardHeader>
              <CardContent>
                <DynamicFieldValidator
                  data={editedData}
                  onFieldChange={handleFieldChange}
                />
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Customer Tab */}
        <TabsContent value="customer" className="flex-1 m-0 p-4 overflow-auto">
          <div className="space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Customer Assignment</CardTitle>
              </CardHeader>
              <CardContent>
                <CompactCustomerSelection
                  selectedCustomerId={queueItem?.customer_id}
                  onCustomerSelect={(customer) => {
                    setEditedData(prev => ({
                      ...prev,
                      customer_id: customer?.id || null
                    }));
                  }}
                />
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Mobile Action Bar */}
      <div className="border-t bg-card p-3">
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={handleSave}
            className="flex-1"
          >
            <Save className="h-4 w-4 mr-2" />
            Save
          </Button>
          <Button 
            onClick={handleComplete}
            className="flex-1"
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            Complete
          </Button>
        </div>
      </div>
    </div>
  );
}